import {Cell, Row, Worksheet} from 'exceljs';
import {
    decorateCell,
    decorateLinkCell,
    decorateSheetTitleCell,
    decorateTableCell,
    decorateTableHeaderCell,
    setCellValue,
} from 'src/modules/downloads/cells/cells';
import {DEFAULT_DATE_FORMAT, HorizontalAlignment, VerticalAlignment} from 'src/modules/downloads/cells/styles';
import {printLogo} from 'src/modules/downloads/commons/print-logo';
import {printMetadata} from 'src/modules/downloads/commons/print-metadata';
import {COLUMN_CAPTIONS, ColumnTypes} from 'src/modules/events/constants';
import {getCriteriaRichText} from 'src/modules/events/download/criteria/get-criteria';
import {Filters} from 'src/modules/events/typedefs';
import {EventBasicInfo} from 'src/openapi/models/EventBasicInfo';

import {formatDate} from '@pb/kit-formatters';

const FIRST_ROW = 1;
const FIRST_ROW_HEIGHT = 30;
const FIRST_DATA_ROW = 10;
const TABLE_HEADER_ROW = 9;
const TABLE_HEADER_HEIGHT = 35;
const SHEET_TITLE = 'Events';
const LINK = 'Link:';
const LINK_CAPTION = 'View Events';
const CRITERIA = 'Criteria:';

const printCriteria = (worksheet: Worksheet, filters: Filters) => {
    worksheet.mergeCells('B4:D6');
    const cell = worksheet.getCell('B4') as Partial<Cell>;
    cell.alignment = {vertical: VerticalAlignment.TOP, wrapText: true};
    const richText = getCriteriaRichText(filters);
    cell.value = {richText};
};

export const printSheetHeader = (worksheet: Worksheet, userName: string, filters: Filters) => {
    const row = worksheet.getRow(FIRST_ROW) as Partial<Row>;
    row.height = FIRST_ROW_HEIGHT;

    const link = (cell: Partial<Cell>) =>
        cell.value = {text: LINK_CAPTION, hyperlink: window.location.href};

    printLogo(worksheet);
    printMetadata(worksheet, userName);
    printCriteria(worksheet, filters);
    decorateSheetTitleCell(worksheet.getCell('E1'), setCellValue(SHEET_TITLE, HorizontalAlignment.CENTER));
    decorateCell(worksheet.getCell('A3'), setCellValue(LINK, HorizontalAlignment.RIGHT));
    decorateLinkCell(worksheet.getCell('B3'), link);
    decorateCell(worksheet.getCell('A4'), setCellValue(CRITERIA, HorizontalAlignment.RIGHT, VerticalAlignment.TOP));
};

export const printTableDataHeader = (worksheet: Worksheet) => {
    const row = worksheet.getRow(TABLE_HEADER_ROW) as Partial<Row>;
    row.height = TABLE_HEADER_HEIGHT;

    const type = COLUMN_CAPTIONS[ColumnTypes.EVENT_TYPE];
    const source = COLUMN_CAPTIONS[ColumnTypes.EVENT_SOURCE];
    const families = COLUMN_CAPTIONS[ColumnTypes.EVENT_FAMILIES];
    const code = COLUMN_CAPTIONS[ColumnTypes.EVENT_CODE];
    const description = COLUMN_CAPTIONS[ColumnTypes.EVENT_DESCRIPTION];
    const attributes = COLUMN_CAPTIONS[ColumnTypes.EVENT_ATTRIBUTES];
    const firstUsageDate = COLUMN_CAPTIONS[ColumnTypes.EVENT_FIRST_USAGE_DATE];
    const lastUsageDate = COLUMN_CAPTIONS[ColumnTypes.EVENT_LAST_USAGE_DATE];

    decorateTableHeaderCell(worksheet.getCell('A9'), 15, setCellValue(type, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('B9'), 20, setCellValue(source, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('C9'), 30, setCellValue(families, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('D9'), 30, setCellValue(code, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('E9'), 65, setCellValue(description, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('F9'), 55, setCellValue(attributes, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('G9'), 20, setCellValue(firstUsageDate, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('H9'), 20, setCellValue(lastUsageDate, HorizontalAlignment.CENTER));
};

export const printTableData = (worksheet: Worksheet, events: Array<EventBasicInfo>) => {
    events.forEach((event, index) => {
        const row = worksheet.getRow(FIRST_DATA_ROW + index);

        const families = event.families
            .map(family => family).join(', ');
        const attributes = event.attributes
            .map(attribute => attribute.name).join(', ');
        const firstUsageDate = event.firstUsageDate ? formatDate(event.firstUsageDate, DEFAULT_DATE_FORMAT) : '';
        const lastUsageDate = event.lastUsageDate ? formatDate(event.lastUsageDate, DEFAULT_DATE_FORMAT) : '';

        decorateTableCell(row, 1, setCellValue(event.type));
        decorateTableCell(row, 2, setCellValue(event.source));
        decorateTableCell(row, 3, setCellValue(families));
        decorateTableCell(row, 4, setCellValue(event.code));
        decorateTableCell(row, 5, setCellValue(event.description));
        decorateTableCell(row, 6, setCellValue(attributes));
        decorateTableCell(row, 7, setCellValue(firstUsageDate, HorizontalAlignment.CENTER));
        decorateTableCell(row, 8, setCellValue(lastUsageDate, HorizontalAlignment.CENTER));
    });
};
